import { ClickAwayListener, Grid, Popper, Tooltip, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { FC, MouseEvent, useId } from 'react';
import Calendar, { CalendarProps } from 'react-calendar';

import useAnchorState from '../hooks/useAnchorState';
import ArrowRight from '../images/arrow-right-dropdown.svg';
import CalendarIcon from '../images/calendar.svg';
import CloseIcon from '../images/close.svg';
import InfoIcon from '../images/info-grey.svg';
import Caption from '../Typography/Caption';
import useStyles from './styles';

/**
 * A stable, unique id for one Datepicker. `useId` returns colons, which a CSS
 * `#id` selector cannot hold unescaped.
 */
const useValueId = (): string => `fds-datepicker-value-${useId().replace(/:/g, '')}`;

export interface DatepickerProps {
  className?: string;
  error?: string;
  /**
   * ID of the element that labels this field (e.g. an adjacent field label),
   * since the trigger isn't a native input a <label htmlFor> can attach to.
   * Pass it: without it the trigger is named by the date or placeholder it
   * shows and the calendar icon's untranslated alt. With it the trigger is
   * named by that label, followed by the selected date when there is one.
   */
  ariaLabelledBy?: string;
  locale: CalendarProps['locale'];
  maxDate?: Date;
  minDate?: Date;
  onDateChange: (value: Date | Date[] | undefined) => void;
  /**
   * `fountain-ui-components` is locale agnostic, and it's up to the consumers to provide
   *  a localized value to render, e.g. `<FormattedDate />`
   */
  renderDate: (value: Date) => React.ReactNode;
  /**
   * Prefer using an example, localized date as a placeholder rather than a string like "mm/dd/yyy"
   */
  renderPlaceholder?: () => React.ReactNode;
  /**
   * If we start allowing this component to leverage date ranges, this type should be
   * updated to: `Date | Date[]`
   */
  value?: Date;
  alignError?: 'left' | 'right';
  canOpen?: boolean;
  tooltipMessage?: string;
  allowRemovingDate?: boolean;
  icons?: {
    calendar: React.ReactElement;
    locked: React.ReactElement;
    clear: React.ReactElement;
    next: React.ReactElement;
    previous: React.ReactElement;
  };
}

export const Datepicker: FC<DatepickerProps> = ({
  value,
  onDateChange,
  minDate,
  maxDate,
  className,
  error,
  ariaLabelledBy,
  locale = 'en-US',
  renderDate,
  renderPlaceholder = () => '',
  alignError = 'left',
  canOpen = true,
  tooltipMessage = '',
  allowRemovingDate = false,
  icons,
  ...popperProps
}) => {
  const classes = useStyles();
  const valueId = useValueId();
  const errorId = `${valueId}-error`;

  const { anchorEl: containerElement, toggle: toggleSelectingDate, handleClose } = useAnchorState();

  const onChange: CalendarProps['onChange'] = selectedDate => {
    if (onDateChange) {
      onDateChange(selectedDate);
    }
    handleClose();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!canOpen || event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleSelectingDate(event);
    }
  };

  const tooltip = () => {
    return (
      <Tooltip title={tooltipMessage}>
        {/* The icon carries the message to a screen reader, which cannot hover it. */}
        {icons ? (
          <span aria-label={tooltipMessage} role="img">
            {icons.locked}
          </span>
        ) : (
          <img alt={tooltipMessage} src={InfoIcon} />
        )}
      </Tooltip>
    );
  };
  const handleClearDate = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // The button unmounts once the value clears, so keep focus on the trigger.
    e.currentTarget.parentElement?.focus();
    if (onDateChange) {
      onDateChange(undefined);
    }
  };

  return (
    <Grid className={classNames(classes.root, icons && classes.callerIcons, className)}>
      <Grid
        alignItems="center"
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
        aria-expanded={canOpen ? !!containerElement : undefined}
        aria-haspopup={canOpen ? 'dialog' : undefined}
        // `aria-labelledby` replaces the name the trigger's content would give
        // it, so it names the selected date too, or the label alone hides it.
        aria-labelledby={ariaLabelledBy && value ? `${ariaLabelledBy} ${valueId}` : ariaLabelledBy}
        className={classNames(classes.dayContainer, {
          active: !!containerElement,
          error: !!error,
          [classes.grayInput]: !canOpen,
        })}
        container
        justify="space-between"
        onClick={canOpen ? toggleSelectingDate : undefined}
        onKeyDown={handleTriggerKeyDown}
        role={canOpen ? 'button' : undefined}
        tabIndex={canOpen ? 0 : undefined}
      >
        <Typography className={value ? classes.value : classes.black} id={valueId}>
          {value ? renderDate(value) : renderPlaceholder()}
        </Typography>
        {/* Not decorative: with no value, placeholder or ariaLabelledBy, this alt
            is the trigger's only name. */}
        {canOpen ? (icons?.calendar ?? <img alt="Calendar" src={CalendarIcon} />) : tooltip()}
        {allowRemovingDate && value && (
          <button
            aria-label="Clear date"
            className={classes.clearButton}
            onClick={handleClearDate}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            type="button"
          >
            {/* Decorative: the button's aria-label names it. */}
            {icons?.clear ?? <img alt="" src={CloseIcon} />}
          </button>
        )}
      </Grid>
      <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClose} touchEvent="onTouchStart">
        <Popper
          anchorEl={containerElement}
          className={classes.calendarContainer}
          open={!!containerElement && canOpen}
          {...popperProps}
        >
          <Calendar
            calendarType="US"
            className={classNames(classes.calendar, {
              visible: !!containerElement,
            })}
            locale={locale}
            maxDate={maxDate}
            minDate={minDate}
            minDetail="decade"
            next2Label={null} // Date | undefined -- year-jump button intentionally disabled, no aria-label needed
            nextAriaLabel="Next month"
            nextLabel={icons?.next ?? <img alt="" src={ArrowRight} />}
            onChange={onChange}
            prev2Label={null} // year-jump button intentionally disabled, no aria-label needed
            prevAriaLabel="Previous month"
            prevLabel={icons?.previous ?? <img alt="" className={classes.leftArrow} src={ArrowRight} />}
            tileClassName={classes.calendarTile}
            value={value}
          />
        </Popper>
      </ClickAwayListener>
      {error && (
        <Caption align={alignError} classes={{ root: classes.errorMessage }} color="error" id={errorId}>
          {error}
        </Caption>
      )}
    </Grid>
  );
};
